import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, MapPin, Calendar as CalendarIcon, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PortfolioSection = () => {
  const [weddings, setWeddings] = useState([]);
  const [selectedWedding, setSelectedWedding] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        const response = await axios.get(`${API}/portfolio`);
        setWeddings(response.data);
      } catch (error) {
        console.error('Error fetching portfolio:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPortfolio();
  }, []);

  const openLightbox = (wedding, index) => {
    setSelectedWedding(wedding);
    setCurrentImageIndex(index);
  };

  const closeLightbox = () => {
    setSelectedWedding(null);
  };

  const nextImage = () => {
    const newIndex = currentImageIndex === weddings.length - 1 ? 0 : currentImageIndex + 1;
    setCurrentImageIndex(newIndex);
    setSelectedWedding(weddings[newIndex]);
  };

  const prevImage = () => {
    const newIndex = currentImageIndex === 0 ? weddings.length - 1 : currentImageIndex - 1;
    setCurrentImageIndex(newIndex);
    setSelectedWedding(weddings[newIndex]);
  };

  if (loading) {
    return (
      <section id="portfolio" className="section-padding bg-transparent">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 size={40} className="animate-spin text-[#C4B5A0]" />
        </div>
      </section>
    );
  }

  if (weddings.length === 0) {
    return null;
  }

  return (
    <section id="portfolio" className="section-padding bg-transparent">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-caption text-[#C4B5A0] mb-4">
            {t('portfolio.label')}
          </p>
          <h2 className="heading-secondary text-4xl md:text-5xl text-[#2C2C2C] mb-4">
            {t('portfolio.title')}
          </h2>
          <div className="divider" />
          <p className="text-body text-[#6B4E37]">
            {t('portfolio.subtitle')}
          </p>
        </div>

        {/* Portfolio Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {weddings.map((wedding, index) => (
            <div
              key={wedding.id}
              className="group cursor-pointer"
              onClick={() => openLightbox(wedding, index)}
            >
              <div className="relative aspect-[4/5] rounded-lg overflow-hidden shadow-md">
                <img
                  src={wedding.image}
                  alt={`Casamento ${wedding.couple}`}
                  className="img-cover group-hover:scale-110 transition-transform duration-700"
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#2C2C2C]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
                  <h3 className="font-['Cormorant_Garamond'] text-2xl text-white mb-2">
                    {wedding.couple}
                  </h3>
                  <div className="flex items-center gap-2 text-[#D4C4B0] text-sm">
                    <MapPin size={14} />
                    <span>{wedding.location}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {selectedWedding && (
        <div
          className="fixed inset-0 z-50 bg-[#2C2C2C]/95 flex items-center justify-center p-4 animate-fade-in"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 text-white hover:text-[#C4B5A0] transition-colors"
            aria-label="Fechar"
          >
            <X size={32} />
          </button>

          {/* Navigation */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              prevImage();
            }}
            className="absolute left-4 md:left-8 text-white hover:text-[#C4B5A0] transition-colors"
            aria-label="Anterior"
          >
            <ChevronLeft size={40} />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              nextImage();
            }}
            className="absolute right-4 md:right-8 text-white hover:text-[#C4B5A0] transition-colors"
            aria-label="Próximo"
          >
            <ChevronRight size={40} />
          </button>

          {/* Content */}
          <div
            className="max-w-4xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="aspect-[3/2] rounded-lg overflow-hidden mb-6">
              <img
                src={selectedWedding.image}
                alt={`Casamento ${selectedWedding.couple}`}
                className="img-cover"
              />
            </div>
            <div className="text-center">
              <h3 className="font-['Cormorant_Garamond'] text-3xl text-white mb-3">
                {selectedWedding.couple}
              </h3>
              <div className="flex items-center justify-center gap-6 text-[#D4C4B0] text-sm mb-4">
                <div className="flex items-center gap-2">
                  <MapPin size={16} />
                  <span>{selectedWedding.location}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CalendarIcon size={16} />
                  <span>{selectedWedding.date}</span>
                </div>
              </div>
              <p className="text-[#8B7D6B]">{selectedWedding.description}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PortfolioSection;
